import DateJS, { format2DateStr, format2TimeStamp } from '@/utils/date';
import { MemberType, MainOrderStatus, OccType } from '@/config/constant';
import { parseFen2Yuan, parseYuan2Fen } from '@/utils/number';
import getRandomId from '@/utils/uuid';
import checkInService from '@/api/checkIn';
import { fetchOrderDetailBaseInfo } from '@/api/order';
import {
  ReducerState,
  MainState,
  ReserveCheckInRoomInfo,
} from '@/redux/reducers/reservation/checkInByReserve';
import { createGuestItem, flatPriceList } from '@/redux/actions/reservation/helper';
import { RoomPriceInfo } from '@/redux/reducers/reservation/base.d';
import { createTargetGetter, TargetType } from '@/components/Reservation/PopupTarget';
import { parseAreaMobileStr } from '@/components/BaseMobileInput/helper';

export const getStateFromOrder: (resvNo: string, recNos: string[]) => Promise<MainState> = async (
  resvNo,
  recNos,
) => {
  const [detailRes, summaryRes] = await Promise.all([
    checkInService.getCheckInOrderInfo({ resvNo, recNos }),
    fetchOrderDetailBaseInfo({ resvNo }),
  ]);
  const orderInfo = detailRes.data;
  const defaultOccType = orderInfo.occType;
  const roomList = orderInfo.subOrderCreateRespList || [];
  const guestList = orderInfo.subOrderOccRespList || [];
  const ownerInfo = guestList.find((item) => item.memberType === MemberType.HouseOwner); // 房主
  const rentRoomInfos: ReserveCheckInRoomInfo[] = []; // 房间信息
  roomList.forEach((item) => {
    const roomPriceList: RoomPriceInfo[] = []; // 房间房价
    (item.subOrderRoomPriceCreateRespList || []).forEach((dayInfo) => {
      const {
        rentDate,
        price,
        couponAmount,
        couponName,
        couponNo,
        couponType,
        couponWyuid,
        crsCouponAmount,
        couponSubType,
        marketStrategyPrice,
      } = dayInfo;
      roomPriceList.push({
        rentDateId: getRandomId(),
        occType: defaultOccType,
        rentDate: format2DateStr(rentDate),
        inputPrice: parseFen2Yuan(price),
        couponAmount,
        couponValue: parseFen2Yuan(couponAmount),
        couponNo,
        couponName,
        couponType,
        couponWyuid,
        crsCouponAmount,
        couponSubType,
        marketStrategyPrice,
      });
    });
    rentRoomInfos.push({
      indexId: getRandomId(),
      roomTypeId: item.roomTypeId,
      roomTypeName: item.roomTypeName,
      // 待排房 没有roomId和roomNo
      roomId: item.roomId,
      roomNo: item.roomNo,
      deposit: '',
      guestList: [],
      roomPriceList,
      recNo: item.recNo,
      priceChannel: item.priceChannel,
    });
  });
  // 原订单房态为入住中 代表当前为预订转追加入住
  const isAppendMode = summaryRes.data.resvStatus === MainOrderStatus.CheckinOrder;
  // 钟点房
  const isTimeRent = defaultOccType === OccType.TimeRENT;
  return {
    commonInfo: {
      occType: defaultOccType,
      memberLevel: orderInfo.memberLevel,
      isAppendMode,
      mainOrderId: resvNo,
    },
    allGuestList:
      isAppendMode && ownerInfo
        ? [
            createGuestItem({
              memberType: MemberType.HouseOwner,
              name: ownerInfo.name,
              mobile: ownerInfo.mobile,
              certType: ownerInfo.certType,
              certNo: ownerInfo.certNo,
              wyuId: ownerInfo.wyuId,
              remark: ownerInfo.memberRemark,
              // memberFlag: ownerInfo.memberFlag,
              memberLevel: ownerInfo.memberLevel,
              balance: ownerInfo.balance,
            }),
            createGuestItem({ memberType: MemberType.CheckIn }),
          ]
        : [createGuestItem({ memberType: MemberType.HouseOwner })],
    rentTimeInfo: {
      occType: defaultOccType,
      // 20240905 钟点房预抵时间取当前 预离+入住时长
      expectedArrivalTime: isTimeRent ? DateJS() : DateJS(orderInfo.expectedArrivalTime),
      rentTime: orderInfo.occDuration,
      expectedDepartureTime: isTimeRent
        ? DateJS().add(orderInfo.occDuration, 'hour')
        : DateJS(orderInfo.expectedDepartureTime),
    },
    channelInfo: {
      channelId: orderInfo.sourceChannelId,
      channelName: orderInfo.sourceChannelName,
      strategyId: orderInfo.priceStrategyId,
      strategyName: orderInfo.priceStrategyName,
      channelOrderNo: orderInfo.channelOrderNo,
      otaPaymentAmount: parseFen2Yuan(orderInfo.otaPaymentAmount),
      invoiceAmount: '',
    },
    rentRoomInfos,
    remarkInfo: {
      remark: orderInfo.remark,
      checkInRemark: orderInfo.checkinRemark,
    },
  };
};

export const formatToSubmitData: (data: ReducerState) => { [key: string]: unknown } = (data) => {
  const { commonInfo, channelInfo, rentRoomInfos, allGuestList, roomPriceList, remarkInfo } = data;
  const priceStrategyId = channelInfo.strategyId;
  // 宾客入住房间信息
  const guestRoomIdMap: {
    [key: string]: { roomId: number; roomNo: string; roomTypeId: number };
  } = {};
  const { mainOrderId, isAppendMode } = commonInfo;
  const { cachePriceMap } = flatPriceList(roomPriceList);
  const subOrderCreateVOS = (rentRoomInfos || []).map((room) => {
    const {
      roomId,
      roomNo,
      roomTypeId,
      roomTypeName,
      roomPriceList: itemPriceList,
      guestList,
      deposit,
      recNo,
    } = room;
    const strategyPriceMap = cachePriceMap[roomTypeId] || {};

    (guestList || []).forEach((guestId) => {
      // 宾客所在房间信息
      guestRoomIdMap[guestId] = { roomId, roomNo, roomTypeId };
    });

    return {
      recNo,
      roomId,
      roomNo,
      roomTypeId,
      roomTypeName,
      deposit: parseYuan2Fen(deposit),
      subOrderRoomPriceCreateVOS: (itemPriceList || []).map((dayInfo) => {
        const {
          rentDate,
          inputPrice,
          couponAmount,
          couponName,
          couponNo,
          couponType,
          couponWyuid,
          crsCouponAmount,
          couponSubType,
          marketStrategyPrice,
        } = dayInfo;
        return {
          rentDate: format2TimeStamp(rentDate),
          price: parseYuan2Fen(inputPrice),
          priceStrategyId,
          strategyPrice: parseYuan2Fen(strategyPriceMap[rentDate]),
          couponAmount,
          couponName,
          couponNo,
          couponType,
          couponWyuid,
          crsCouponAmount,
          couponSubType,
          marketStrategyPrice,
        };
      }),
    };
  });
  const subOrderOccCreateVOS = [];
  // 是否只有一件房间 只有一间房时 默认所有宾客都住这一间
  const isOnlyOneRoom = subOrderCreateVOS.length === 1;
  (allGuestList || []).forEach((item) => {
    const { name, indexId, memberType } = item;
    if ((memberType === MemberType.HouseOwner && isAppendMode) || !name) return; // 追加入住 不提交房主
    const { roomId, roomNo } = isOnlyOneRoom ? subOrderCreateVOS[0] : guestRoomIdMap[indexId] || {};
    const { mobileStr } = parseAreaMobileStr(item.mobile);
    subOrderOccCreateVOS.push({
      birthday: item.birthday,
      certNo: item.certNo,
      certType: item.certType,
      certificateEndTime: item.certificateEndTime,
      certificateLocation: item.certificateLocation,
      certificateStartTime: item.certificateStartTime,
      issuingAuthority: item.issuingAuthority,
      idCardReadStatus: item.source === 'READ' ? 1 : 2,
      // memberFlag: item.memberFlag,
      memberLevel: item.memberLevel,
      mobile: mobileStr ? item.mobile : null,
      email: item.email,
      nation: item.nation,
      remark: item.remark,
      sex: item.sex,
      wyuId: item.wyuId,
      memberType,
      name,
      roomId,
      roomNo,
    });
  });
  return {
    resvNo: mainOrderId,
    remark: remarkInfo.remark,
    checkinRemark: remarkInfo.checkInRemark,
    subOrderCreateVOS,
    subOrderOccCreateVOS,
    type: isAppendMode ? 2 : 1, // 1-预订转入住，2-预订转追加入住
  };
};

// 定位节点
export const getPopupContainer = createTargetGetter(TargetType.CheckInByReserve);
